import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  convertToPercentageString,
  checkUnifiedAsset,
  getAutoGeneratedObject,
  getAutogeneratedEpisodeString,
} from "../../shared/utils";
import { getAVSKeyArtImage, handleImageError } from "../../shared/utils/image";
import constants from "../../shared/constants";
import { getEpisodeInfo } from "../../shared/utils/feedHelper";
import { getItemDuration } from "../../shared/utils/swimlane";
import { calculateRecordingProgressPercentage } from "../../shared/utils/recordingHelper";
import middleware from "../../shared/middleware";
import playerConstants from "../../shared/constants/player";
import {
  SidebarItem as VodSidebarItem,
  SidebarItemThumbnail as VodSidebarItemThumbnail,
  SidebarThumbnailImage as VodSidebarThumbnail,
  SidebarOnNowPlay as VodSidebarOnNowPlay,
  SidebarProgressBarContainer as VodSidebarProgressBarContainer,
  SidebarProgressBar as VodSidebarProgressBar,
  SidebarItemDescription as VodSidebarItemDescription,
  SidebarItemDescriptionTitle as VodSidebarItemDescriptionTitle,
  VodSidebarItemDateTime,
} from "../SideBarListItem/SideBarListItem.styles";
import { useTheme } from "styled-components";

const { ASSET_TYPES } = playerConstants;
const { WATCH_OPTIONS, PAGE_CONTENT_ITEM_TYPES } = constants;
const { getContentUserData } = middleware;
const icon = process.env.PUBLIC_URL + "/images/swimlane-landscape-324px.jpg";

const VodSidebarListItem = ({
  program,
  index,
  onEpisodeItemClick,
  currentEpisodeId,
  divElRef,
  divFirstElRef,
  onHoverIn,
  onHoverOut,
  vodProgress,
  isRecordingPlayer,
  appProvider,
  channelMapInfo,
  seasonEntitlements,
  isCPVRBookmarksEnabled,
  showRecordedEpisodeInstancesModal,
  currentProgramUmId,
}) => {
  const theme = useTheme();
  const screenWidth = window.screen.availWidth;
  const { t: translate } = useTranslation();
  const catalogInfo = program?.metadata ?? null;
  const [episodeInfo, setEpisodeInfo] = useState(null);
  const [isUnifiedAsset, setIsUnifiedAsset] = useState(true);
  const [itemProgress, setItemProgress] = useState(0);
  const { isTeamRecording, hasMultipleEventInstances } = program?.metadata || {};

  const progressRef = useRef(null); // ref to capture the latest progress of an episode if already played in the current video player session

  useEffect(() => {
    setIsUnifiedAsset(
      checkUnifiedAsset(isRecordingPlayer ? program?.metadata?.extendedMetadata?.dlum : program?.metadata)
    );
  }, [program, isRecordingPlayer]);

  useEffect(() => {
    if (episodeInfo?.metadata?.contentId === parseInt(currentEpisodeId)) {
      setItemProgress(vodProgress);
      progressRef.current = vodProgress;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vodProgress]);

  useEffect(() => {
    const fetchUserData = async (episodeId) => {
      const data = episodeId ? await getContentUserData(appProvider, episodeId, PAGE_CONTENT_ITEM_TYPES.vod) : {};
      if (data.containers && data.containers.length > 0) setEpisodeInfo(data.containers[0]);
    };
    if (program) {
      if (!isUnifiedAsset) {
        fetchUserData(program?.id);
      } else {
        setEpisodeInfo(
          isRecordingPlayer
            ? program
            : seasonEntitlements?.length > 0 &&
                seasonEntitlements.find(
                  (entitlement) => entitlement?.uaId === program?.metadata?.extendedMetadata?.dlum?.uaId
                )
        );
      }
    }
    return () => setEpisodeInfo(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appProvider, program, isUnifiedAsset, currentEpisodeId, seasonEntitlements]);

  useEffect(() => {
    let progressTime = null;
    if (isRecordingPlayer) {
      progressTime = isCPVRBookmarksEnabled
        ? calculateRecordingProgressPercentage(program, program?.bookmarks?.[0] || program?.metadata?.bookmarks?.[0])
        : 0;
    } else if (episodeInfo?.metadata?.contentId !== parseInt(currentEpisodeId)) {
      progressTime = (episodeInfo?.metadata?.bookmarks?.[0]?.startDeltaTime / program?.metadata?.duration) * 100 ?? 0;
    }
    setItemProgress(progressTime);
  }, [episodeInfo, currentEpisodeId, isRecordingPlayer, program, isCPVRBookmarksEnabled]);

  if (episodeInfo?.metadata?.contentId !== parseInt(currentEpisodeId)) {
    divElRef = null;
  }
  if (index !== 0) {
    divFirstElRef = null;
  }

  const isUpcoming = episodeInfo?.metadata?.airingStartTime > moment().valueOf(); // check if the episode is upcoming

  const canPlay = useCallback(() => {
    if (isUnifiedAsset) {
      const isUserEntitled = isRecordingPlayer
        ? isRecordingPlayer
        : seasonEntitlements?.length > 0 &&
          seasonEntitlements.find(
            (entitlement) => entitlement?.uaId === program?.metadata?.extendedMetadata?.dlum?.uaId
          )?.entitlement?.hasTechPackage;
      if (isUpcoming) return false; // upcoming episodes are not playable
      return isUserEntitled;
    } else {
      const assets = episodeInfo?.entitlement?.assets ?? null;
      if (assets && assets.length) {
        return (
          !isUpcoming &&
          assets.some(
            (asset) =>
              asset.assetType.toLowerCase() === ASSET_TYPES.MASTER && asset.rights.toLowerCase() === WATCH_OPTIONS.watch
          )
        ); // episode is playable if it is not upcoming and has at least one master asset with watch rights
      } else return false;
    }
  }, [episodeInfo, isRecordingPlayer, isUnifiedAsset, isUpcoming, program, seasonEntitlements]);

  const getEpisodeString = () => {
    if (catalogInfo) {
      const uaEpisodeObject = getAutoGeneratedObject(catalogInfo);
      let episodeInfoString = getAutogeneratedEpisodeString(uaEpisodeObject, catalogInfo, true);
      if (!isRecordingPlayer) {
        // Remove first instance of season and episode number prefixes
        episodeInfoString = episodeInfoString.replace(/S\d+\s?/, ""); // Remove season number
        episodeInfoString = episodeInfoString.replace(/E\s?/, ""); // Remove episode prefix

        // Replace first instance of colon with period
        episodeInfoString = episodeInfoString.replace(/:/, ".");
      }
      return episodeInfoString;
    }
  };

  const onSidebarItemClick = useCallback(() => {
    if (isTeamRecording && hasMultipleEventInstances) {
      showRecordedEpisodeInstancesModal({ program, episodeInfo, canPlay: canPlay(), index });
    } else {
      onEpisodeItemClick(episodeInfo, canPlay(), false, index, null, true);
    }
  }, [
    isTeamRecording,
    hasMultipleEventInstances,
    showRecordedEpisodeInstancesModal,
    program,
    episodeInfo,
    canPlay,
    index,
    onEpisodeItemClick,
  ]);

  const isPlaying = useMemo(() => {
    if (
      isTeamRecording &&
      hasMultipleEventInstances &&
      episodeInfo?.metadata?.extendedMetadata?.dlum?.umId === currentProgramUmId
    ) {
      return true;
    }
    if (episodeInfo?.metadata?.contentId === parseInt(currentEpisodeId)) {
      return true;
    }
    return false;
  }, [currentEpisodeId, currentProgramUmId, episodeInfo, hasMultipleEventInstances, isTeamRecording]);

  return program ? (
    <VodSidebarItem
      theme={theme}
      width={screenWidth}
      ref={divElRef}
      $isEpisodeList
      $status={isPlaying ? "playing" : !canPlay() ? "nonPlayable" : ""}
      $playing={isPlaying ? theme.colours.sharkGrayBackground : null}
      onMouseEnter={() => onHoverIn(program)}
      onMouseLeave={() => onHoverOut()}
      onClick={onSidebarItemClick}
    >
      <VodSidebarItemThumbnail theme={theme} width={screenWidth} ref={divFirstElRef} $isEpisodeList>
        <VodSidebarThumbnail
          src={getAVSKeyArtImage(program.metadata, "16x9")}
          alt=""
          data-testid="thumbnail"
          width="100%"
          height="100%"
          $minWidth={screenWidth <= 768 ? "75px" : screenWidth <= 1366 ? "100px" : "170px"}
          onError={(e) => handleImageError(e, icon)}
        />
        <>
          {episodeInfo?.metadata?.contentId !== parseInt(currentEpisodeId) &&
          (episodeInfo?.entitlement?.hasTechPackage || isRecordingPlayer) &&
          !isUpcoming ? (
            <VodSidebarOnNowPlay
              $screen={screenWidth}
              src={process.env.PUBLIC_URL + "/images/CTA-PlayCircle.svg"}
              alt=""
            />
          ) : null}
          {!(isRecordingPlayer && episodeInfo?.metadata?.contentId === parseInt(currentEpisodeId)) &&
            (progressRef.current || itemProgress) > 0 && (
              <VodSidebarProgressBarContainer theme={theme}>
                <VodSidebarProgressBar
                  width={convertToPercentageString(progressRef.current || itemProgress)}
                ></VodSidebarProgressBar>
              </VodSidebarProgressBarContainer>
            )}
        </>
      </VodSidebarItemThumbnail>

      <VodSidebarItemDescription theme={theme} width={screenWidth} $isEpisodeList>
        <VodSidebarItemDescriptionTitle>{getEpisodeString()}</VodSidebarItemDescriptionTitle>
        <VodSidebarItemDateTime theme={theme} width={screenWidth}>
          {getEpisodeInfo(program, channelMapInfo)}
        </VodSidebarItemDateTime>
        <VodSidebarItemDateTime theme={theme} width={screenWidth}>
          {!isUpcoming ? getItemDuration(catalogInfo.duration) : translate("upcoming")}
        </VodSidebarItemDateTime>
      </VodSidebarItemDescription>
    </VodSidebarItem>
  ) : (
    <></>
  );
};

export default VodSidebarListItem;
